"use client";

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

interface SupportAgentConfig {
  /** If true, hide the default support agent entirely */
  hidden: boolean;
  /** Custom agent ID to use instead of the default */
  customAgentId?: string;
  /** Custom position for the agent */
  customPosition?: "center" | "right";
}

interface SupportAgentConfigContextType {
  config: SupportAgentConfig;
  /**
   * Set page-level config. Call with no args to reset to defaults.
   * The config persists until explicitly reset or the component unmounts.
   */
  setConfig: (config?: Partial<SupportAgentConfig>) => void;
  /** Whether there's an active call - if true, agent should stay visible */
  hasActiveCall: boolean;
  /** Set the active call state */
  setHasActiveCall: (active: boolean) => void;
}

const defaultConfig: SupportAgentConfig = {
  hidden: false,
};

const SupportAgentConfigContext = createContext<SupportAgentConfigContextType | null>(null);

export function useSupportAgentConfig() {
  const context = useContext(SupportAgentConfigContext);
  if (!context) {
    throw new Error("useSupportAgentConfig must be used within SupportAgentConfigProvider");
  }
  return context;
}

/**
 * Hook for pages to control the support agent visibility/config.
 * Automatically resets config when the component unmounts.
 */
export function useSetSupportAgentConfig(pageConfig: Partial<SupportAgentConfig>) {
  const { setConfig } = useSupportAgentConfig();

  useEffect(() => {
    setConfig(pageConfig);
    return () => {
      // Reset to default when page unmounts
      setConfig();
    };
  }, [setConfig, pageConfig.hidden, pageConfig.customAgentId, pageConfig.customPosition]);
}

/**
 * Component for pages to hide the support agent.
 * Simply render this component anywhere in your page to hide the default support agent.
 */
export function HideSupportAgent() {
  useSetSupportAgentConfig({ hidden: true });
  return null;
}

export function SupportAgentConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfigState] = useState<SupportAgentConfig>(defaultConfig);
  const [hasActiveCall, setHasActiveCall] = useState(false);

  const setConfig = useCallback((newConfig?: Partial<SupportAgentConfig>) => {
    if (!newConfig) {
      setConfigState(defaultConfig);
    } else {
      setConfigState(prev => ({ ...prev, ...newConfig }));
    }
  }, []);

  return (
    <SupportAgentConfigContext.Provider value={{ config, setConfig, hasActiveCall, setHasActiveCall }}>
      {children}
    </SupportAgentConfigContext.Provider>
  );
}
